import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { BoardResponse, CaseView, Role } from '@chemoflow/contracts';
import { detectChanges, isNoticeFor } from '@chemoflow/domain';
import { BOARD_KEY } from '../api/hooks.js';
import {
  describeChange,
  MAX_INDIVIDUAL_NOTICES,
  summaryNotice,
  type Notice,
} from '../lib/notices.js';
import { useUi } from './ui.js';

/** 他端末の変化を知らせるトーストは、自分の操作の「戻す」トーストより少し長めに出す。 */
const NOTICE_MS = 6000;

/**
 * 他の端末で状態が変わったら、トーストで知らせる。
 *
 * ・取得（ポーリング）で入ってきた結果だけを前回と比べる。自分の操作は応答をそのままキャッシュへ
 *   書き込む（setQueryData＝manual）ので、その更新は比較の基準を進めるだけで通知しない
 * ・初回の取得、ログアウト直後、名簿の追加・削除、日付の切り替えは通知しない（detectChanges 参照）
 * ・職種ごとに作業に関わる変化だけを知らせる（NOTICE_KINDS_BY_ROLE）
 */
export function useRemoteChangeNotices(role: Role, enabled: boolean): void {
  const qc = useQueryClient();
  const { toast } = useUi();

  useEffect(() => {
    if (!enabled) return;
    const show = (n: Notice) =>
      toast(
        <>
          {n.title}
          <small>{n.sub}</small>
        </>,
        { icon: n.icon, ms: NOTICE_MS },
      );

    let prev: CaseView[] | undefined = qc.getQueryData<BoardResponse>(BOARD_KEY)?.cases;
    return qc.getQueryCache().subscribe((event) => {
      if (event.query.queryKey[0] !== BOARD_KEY[0]) return;
      if (event.type === 'removed') {
        prev = undefined;
        return;
      }
      if (event.type !== 'updated' || event.action.type !== 'success') return;
      const next = (event.query.state.data as BoardResponse | undefined)?.cases;
      if (!next) return;

      if (!event.action.manual && prev) {
        const changes = detectChanges(prev, next).filter((c) => isNoticeFor(role, c.kind));
        if (changes.length > MAX_INDIVIDUAL_NOTICES) show(summaryNotice(changes.length));
        else for (const c of changes) show(describeChange(c));
      }
      prev = next;
    });
  }, [qc, toast, role, enabled]);
}
